import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { formatDate } from '../../../../utils/formatters';

const ScheduledMeetingsSection = ({ meetings = [] }) => {
  const intl = useIntl();

  if (!meetings || meetings.length === 0) {
    return null;
  }

  // Get meeting format icon
  const getMeetingFormatIcon = (format) => {
    switch (format) {
      case 'phone':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        );
      case 'video':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      case 'in_person':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        );
    }
  };

  // Get meeting format label
  const getMeetingFormatLabel = (format) => {
    switch (format) {
      case 'phone':
        return intl.formatMessage({ id: 'succession.profile_drawer.scheduled_meetings_section.format_phone' });
      case 'video':
        return intl.formatMessage({ id: 'succession.profile_drawer.scheduled_meetings_section.format_video' });
      case 'in_person':
        return intl.formatMessage({ id: 'succession.profile_drawer.scheduled_meetings_section.format_in_person' });
      default:
        return intl.formatMessage({ id: 'succession.profile_drawer.scheduled_meetings_section.format_default' });
    }
  };

  // Get status badge
  const getMeetingStatusBadge = (meeting) => {
    const startTime = new Date(meeting.start_time);
    const now = new Date();

    if (startTime < now) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
          {intl.formatMessage({ id: 'succession.profile_drawer.scheduled_meetings_section.status_completed' })}
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
          {intl.formatMessage({ id: 'succession.profile_drawer.scheduled_meetings_section.status_upcoming' })}
        </span>
      );
    }
  };

  return (
    <div className="bg-succession-bg-secondary rounded-lg border border-succession-border overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-succession-border">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-1">
            <svg className="h-6 w-6 text-succession-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-succession-text-primary">
              {intl.formatMessage({ id: 'succession.profile_drawer.scheduled_meetings_section.scheduled_meetings' })}
            </h3>
            <p className="text-sm text-succession-text-muted mt-1">
              {meetings.length !== 1
                ? intl.formatMessage({ id: 'succession.profile_drawer.scheduled_meetings_section.meeting_count_other' }, { count: meetings.length })
                : intl.formatMessage({ id: 'succession.profile_drawer.scheduled_meetings_section.meeting_count_one' }, { count: meetings.length })}
            </p>
          </div>
        </div>
      </div>

      {/* Meetings List */}
      <div className="px-6 py-4">
        <div className="space-y-4">
          {meetings.map((meeting, index) => (
            <div 
              key={meeting.id || index} 
              className="bg-succession-bg-primary rounded-lg border border-succession-border p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <div className="flex-shrink-0 mt-1 text-succession-primary">
                    {getMeetingFormatIcon(meeting.format)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-sm font-semibold text-succession-text-primary">
                        {getMeetingFormatLabel(meeting.format)}
                      </h4>
                      {getMeetingStatusBadge(meeting)}
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-succession-text-muted">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>{formatDate(meeting.start_time)}</span>
                      </div>
                      {meeting.duration && (
                        <div className="flex items-center gap-2 text-sm text-succession-text-muted">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>{meeting.duration} {intl.formatMessage({ id: 'succession.profile_drawer.scheduled_meetings_section.minutes_suffix' })}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-3 bg-succession-bg-primary border-t border-succession-border">
        <div className="flex items-center text-xs text-succession-text-muted">
          <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {intl.formatMessage({ id: 'succession.profile_drawer.scheduled_meetings_section.keep_informed' })}
        </div>
      </div>
    </div>
  );
};

ScheduledMeetingsSection.propTypes = {
  meetings: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    start_time: PropTypes.string,
    duration: PropTypes.number,
    format: PropTypes.oneOf(['phone', 'video', 'in_person']),
  })),
};

export default ScheduledMeetingsSection;

